import { makeApiCall } from "src/api/apiRequests";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import ValidationMessage from "src/helpers/ValidationMessage";
import Loading from "src/helpers/Loading";

interface Permission {
  permission_key: string;
  description: string;
}
interface GroupedPermissions {
  [key: string]: Permission[];
}

const AddRole = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validations, setValidations] = useState<Record<string, string>>({});
  const [validationPermission, setValidationPermission] = useState<any>({});
  const [irrelevantPermission, setIrrelevantPermission] = useState<any>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [verifiedPermission, setVerifiedPermission] = useState<string[]>([]);
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [openAccordions, setOpenAccordions] = useState<string[]>([]);

  const getPermissions = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.permissionList,
        "GET",
        API_HEADERS.authenticated
      );
      setPermissions(response.permissions);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
    }
  };

  // Function to group permissions by prefix
  const groupPermissionsByPrefix = (
    permissions: Permission[]
  ): GroupedPermissions => {
    return permissions.reduce(
      (acc: GroupedPermissions, permission: Permission) => {
        const prefix = permission.permission_key.split("-")[0]; 

        if (!acc[prefix]) {
          acc[prefix] = [];
        }

        acc[prefix].push(permission);
        return acc;
      },
      {}
    );
  };

  // Function to handle checked permissions
  const handleChnage = (permissionId: string, isChecked: boolean) => {
    if (isChecked) {
      setVerifiedPermission((prev) => [...prev, permissionId]);
    } else {
      setVerifiedPermission((prev) => prev.filter((id) => id !== permissionId));
    }
  };

  const storeRoles = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setBtnLoading(true);
    const formData = {
      name: role,
      permissions: verifiedPermission,
    };
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.storeRole,
        "POST",
        API_HEADERS.authenticated,
        formData
      );
      FLASH_MESSAGES.successMsg(response.success);
      navigate(-1);
    } catch (error: any) {
      if (error.response.status === 422) {
        setValidations(error.response.data);
        
      } else if (error.response.status === 403) {
        // Valid permissions
        error.response.data.required_permission
          ? setValidationPermission(error.response.data.required_permission)
          : setValidationPermission({});

        // Irrelevant permissions
       if (Array.isArray(error.response.data.irrelevant_permissions)) {
         setIrrelevantPermission(error.response.data.irrelevant_permissions);
       } else {
         setIrrelevantPermission([]);
       }

        FLASH_MESSAGES.errorMsg(error.response.data.error);
      } else {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  useEffect(() => {
    getPermissions();
  }, []);

  // Automatically open accordions containing irrelevant or validation permissions
  useEffect(() => {
    const openGroups = Object.keys(groupedPermissions).filter((group) =>
      groupedPermissions[group].some(
        (permission) =>
          irrelevantPermission.includes(permission.permission_key) ||
          Object.keys(validationPermission).some((requiredKey) =>
            validationPermission[requiredKey].includes(
              permission.permission_key
            )
          )
      )
    );
    setOpenAccordions(openGroups); 
  }, [irrelevantPermission, validationPermission, permissions]);

 
  const groupedPermissions = groupPermissionsByPrefix(permissions);

  return (
    <>
      <form onSubmit={storeRoles}>
        <p className="popup_title mb-4">{t("add_staff_subtitle")}</p>

        <div className="form-block col-12 col-sm-6 col-md-2 mb-5">
          <label className="required">{t("label_role")}</label>
          <input
            type="text"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            name="role"
            className={"form-input" + (validations.name ? " input-error" : "")}
            placeholder={t("label_role")}
          />
          {validations.name && (
            <ValidationMessage message={validations.name[0]} />
          )}
        </div>

        {loading ? (
          <div className="permission-loading mt-3">
            <Loading />
          </div>
        ) : (
          <>
          
            <div className="accordion custom-accordion">
              {Object.keys(groupedPermissions).map((group, index) => (
                <div className="accordion-custom-item" key={group}>
                  <button
                    className={`accordion-custom-button ${
                      openAccordions.includes(group) ? "" : "collapsed"
                    }`}
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={"#" + group}
                    aria-expanded={openAccordions.includes(group)}
                    aria-controls={group}
                  >
                    <h4>{t("permission_group_" + group)}</h4>
                    <i className="icon arrow-down-icon" />
                  </button>

                  <div
                    id={group}
                    className={`accordion-collapse collapse ${
                      openAccordions.includes(group) ? "show" : ""
                    }`}
                  >
                    <div className="accordion-body">
                      {groupedPermissions[group].map(
                        (permission: Permission) => (
                          <div
                            className="permission-item"
                            key={permission.permission_key}
                          >
                            <div className="permission">
                              <input
                                type="checkbox"
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  boxShadow:
                                    Object.keys(validationPermission).some(
                                      (requiredKey) =>
                                        validationPermission[
                                          requiredKey
                                        ].includes(permission.permission_key)
                                    ) &&
                                    !verifiedPermission.includes(
                                      permission.permission_key
                                    )
                                      ? "0 0 5px 2px rgba(255, 0, 0, 0.3)"
                                      : "",
                                }}
                                className="form-check-input me-4"
                                id={`${permission.permission_key}`}
                                onChange={(e) =>
                                  handleChnage(
                                    permission.permission_key,
                                    e.target.checked
                                  )
                                }
                              />
                              <label htmlFor={permission.permission_key}>
                                {permission.description}
                              </label>
                            </div>

                            {Object.keys(validationPermission).some(
                              (requiredKey) =>
                                validationPermission[requiredKey].includes(
                                  permission.permission_key
                                )
                            ) && (
                              <ValidationMessage
                                message={t("required-mesage-permission")}
                              />
                            )}
                          
                            {irrelevantPermission.includes(
                              permission.permission_key
                            ) && (
                              <ValidationMessage
                                message={t("irrelateble_permission_message")}
                              />
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="d-flex justify-content-end gap-4 mt-5">
              <button
                className="button-white"
                type="button"
                onClick={() => navigate(-1)}
              >
                {t("meal_edit_assortment_cancel_button")}
              </button>
              {btnLoading ? (
                <button className="button-blue has-icon" type="button">
                  <i className="icon spinner-icon" />
                  {t("add_staff_saving")}
                </button>
              ) : (
                <button className="button-blue has-icon" type="submit">
                  <i className="icon plus-icon" />
                  {t("add_staff_save")}
                </button>
              )}
            </div>
          </>
        )}
      </form>
    </>
  );
};

export default AddRole;
